import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, Spinner } from '../../../../common';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { required } from '../../../../antd/form/FormValidation';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useAdjustPhdStudentDeadlineMutation,
  useCorrectPhdStudyInterruptionMutation,
  useCreatePhdStudyInterruptionMutation,
  useGetPhdStudentDeadlinesQuery,
  useGetPhdStudentInterruptionsQuery,
  useResumePhdStudyInterruptionMutation,
} from '../../../../../redux/services/phd';

const DEADLINE_LABELS = {
  individual_plan: 'Individuálny študijný plán',
  annual_credits: 'Ročné kredity',
  annual_evaluation: 'Ročné hodnotenie',
  dissertation_exam_recommended: 'Odporúčaný termín dizertačnej skúšky',
  dissertation_exam_limit: 'Najneskorší termín dizertačnej skúšky',
  standard_defense: 'Štandardný termín obhajoby',
  overstandard_limit: 'Hranica nadštandardného štúdia',
};

const SOURCE_LABELS = {
  rule: 'Pravidlo',
  interruption: 'Prerušenie',
  agreement: 'Individuálna dohoda',
  correction: 'Korekcia',
  legacy: 'Pôvodný údaj',
};

const SOURCE_HELP = {
  rule: 'Dátum vypočítalo platné pravidlo štúdia.',
  interruption: 'Dátum posunulo evidované prerušenie štúdia.',
  agreement: 'Dátum určuje schválená individuálna dohoda.',
  correction: 'Dátum opravuje chybný skorší údaj bez prepisu histórie.',
  legacy: 'Dátum bol prevzatý z pôvodnej evidencie a zostáva auditovateľný.',
};

const STATUS_LABELS = {
  applicable: 'Uplatňuje sa',
  paused: 'Pozastavené',
  not_applicable: 'Neuplatňuje sa',
};

const STATUS_HELP = {
  applicable: 'Termín je platný a používa sa pri kontrole priebehu štúdia.',
  paused:
    'Otvorené prerušenie pozastavilo budúci termín. Po obnovení štúdia vznikne nová platná verzia.',
  not_applicable:
    'Termín sa na tento školský rok alebo stav štúdia nevzťahuje.',
};

const formatDate = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format('DD.MM.YYYY') : '—';

const formatDateTime = (value) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format('DD.MM.YYYY HH:mm')
    : '—';

const toApiDate = (value) => (value ? value.format('YYYY-MM-DD') : null);

const PhdStudentLifecycleSection = ({
  student,
  canManageInterruptions,
  canAdjustDeadlines,
  onLifecycleChanged,
}) => {
  const { t } = useTranslation();
  const [deadlineForm] = Form.useForm();
  const [interruptionForm] = Form.useForm();
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [interruptionAction, setInterruptionAction] = useState(null);
  const studentId = student?.id;

  const { data: deadlines, isLoading: isLoadingDeadlines } =
    useGetPhdStudentDeadlinesQuery(studentId, { skip: !studentId });
  const { data: interruptions, isLoading: isLoadingInterruptions } =
    useGetPhdStudentInterruptionsQuery(studentId, { skip: !studentId });
  const [adjustDeadline, { isLoading: isAdjustingDeadline }] =
    useAdjustPhdStudentDeadlineMutation();
  const [createInterruption, { isLoading: isCreatingInterruption }] =
    useCreatePhdStudyInterruptionMutation();
  const [resumeInterruption, { isLoading: isResumingInterruption }] =
    useResumePhdStudyInterruptionMutation();
  const [correctInterruption, { isLoading: isCorrectingInterruption }] =
    useCorrectPhdStudyInterruptionMutation();
  const { saveSuccess, saveFailed } = useToastMessages();

  const closeDeadlineModal = () => {
    setSelectedDeadline(null);
    deadlineForm.resetFields();
  };

  const openDeadlineModal = (deadline) => {
    setSelectedDeadline(deadline);
    deadlineForm.setFieldsValue({
      effective_date: deadline?.effective_date
        ? dayjs(deadline.effective_date)
        : null,
      source: 'agreement',
      reason: undefined,
      decision_reference: undefined,
    });
  };

  const handleDeadlineSubmit = async () => {
    const values = await deadlineForm.validateFields();

    try {
      await adjustDeadline({
        studentId,
        body: {
          deadline_key: selectedDeadline.deadline_key,
          phd_school_year_id: selectedDeadline.phd_school_year_id ?? null,
          effective_date: toApiDate(values.effective_date),
          source: values.source,
          reason: values.reason,
          decision_reference: values.decision_reference,
        },
      }).unwrap();
      saveSuccess();
      closeDeadlineModal();
      onLifecycleChanged?.();
    } catch (error) {
      saveFailed(error);
    }
  };

  const closeInterruptionModal = () => {
    setInterruptionAction(null);
    interruptionForm.resetFields();
  };

  const openInterruptionModal = (type, record = null) => {
    setInterruptionAction({ type, record });
    interruptionForm.setFieldsValue(
      type === 'resume'
        ? {
            date_resumed: null,
            decision_reference: undefined,
            note: undefined,
          }
        : {
            date_started: record?.date_started
              ? dayjs(record.date_started)
              : null,
            date_resumed: record?.date_resumed
              ? dayjs(record.date_resumed)
              : null,
            reason: record?.reason,
            decision_reference: record?.decision_reference,
            note: record?.note,
            is_exceptional: Boolean(record?.is_exceptional),
            exception_reason: record?.exception_reason,
          },
    );
  };

  const handleInterruptionSubmit = async () => {
    const values = await interruptionForm.validateFields();
    const action = interruptionAction;
    const commonPayload = {
      decision_reference: values.decision_reference,
      note: values.note || null,
    };

    try {
      if (action.type === 'resume') {
        await resumeInterruption({
          id: action.record.id,
          studentId,
          body: {
            ...commonPayload,
            date_resumed: toApiDate(values.date_resumed),
          },
        }).unwrap();
      } else {
        const body = {
          ...commonPayload,
          date_started: toApiDate(values.date_started),
          date_resumed: toApiDate(values.date_resumed),
          reason: values.reason,
          is_exceptional: Boolean(values.is_exceptional),
          exception_reason: values.is_exceptional
            ? values.exception_reason
            : null,
        };

        if (action.type === 'correct') {
          await correctInterruption({
            id: action.record.id,
            studentId,
            body,
          }).unwrap();
        } else {
          await createInterruption({ studentId, body }).unwrap();
        }
      }

      saveSuccess();
      closeInterruptionModal();
      onLifecycleChanged?.();
    } catch (error) {
      saveFailed(error);
    }
  };

  const effectiveDeadlineIds = useMemo(
    () => new Set((deadlines?.effective || []).map((deadline) => deadline.id)),
    [deadlines?.effective],
  );

  const deadlineColumns = [
    {
      title: t('Termín'),
      dataIndex: 'deadline_key',
      key: 'deadline_key',
      render: (value) => t(DEADLINE_LABELS[value] || value),
    },
    {
      title: t('Školský rok'),
      key: 'school_year',
      render: (_, row) =>
        row.school_year
          ? `${dayjs(row.school_year.start_year).format('YYYY')}/${dayjs(
              row.school_year.end_year,
            ).format('YYYY')}`
          : t('Celé štúdium'),
    },
    {
      title: t('Východiskový dátum'),
      dataIndex: 'baseline_date',
      key: 'baseline_date',
      render: formatDate,
    },
    {
      title: t('Vypočítaný dátum'),
      dataIndex: 'calculated_date',
      key: 'calculated_date',
      render: formatDate,
    },
    {
      title: t('Platný dátum'),
      dataIndex: 'effective_date',
      key: 'effective_date',
      render: (value) => <strong>{formatDate(value)}</strong>,
    },
    {
      title: t('Zdroj'),
      dataIndex: 'source',
      key: 'source',
      render: (value) => (
        <span>
          {t(SOURCE_LABELS[value] || value || '—')}
          {SOURCE_HELP[value] ? (
            <PhdHelpHint
              label={t('Vysvetlenie zdroja termínu')}
              title={t(SOURCE_HELP[value])}
            />
          ) : null}
        </span>
      ),
    },
    {
      title: t('Stav termínu'),
      dataIndex: 'status',
      key: 'status',
      render: (value) => (
        <span>
          <Tag color={value === 'applicable' ? 'green' : 'orange'}>
            {t(STATUS_LABELS[value] || value || '—')}
          </Tag>
          {STATUS_HELP[value] ? (
            <PhdHelpHint
              label={t('Vysvetlenie stavu termínu')}
              title={t(STATUS_HELP[value])}
            />
          ) : null}
        </span>
      ),
    },
    {
      title: t('Verzia pravidla'),
      dataIndex: 'rule_version',
      key: 'rule_version',
      render: (value) => value || '—',
    },
    {
      title: t('Verzia'),
      key: 'version_state',
      render: (_, row) =>
        effectiveDeadlineIds.has(row.id) ? (
          <Tag color="green">{t('Platná')}</Tag>
        ) : (
          <Tag>{t('Nahradená')}</Tag>
        ),
    },
    ...(canAdjustDeadlines
      ? [
          {
            title: t('Akcie'),
            key: 'actions',
            render: (_, row) =>
              effectiveDeadlineIds.has(row.id) ? (
                <Button size="small" onClick={() => openDeadlineModal(row)}>
                  {t('Upraviť termín dohodou')}
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  const interruptionColumns = [
    {
      title: t('Od'),
      dataIndex: 'date_started',
      key: 'date_started',
      render: formatDate,
    },
    {
      title: t('Návrat do štúdia'),
      dataIndex: 'date_resumed',
      key: 'date_resumed',
      render: (value) =>
        value ? formatDate(value) : <Tag color="orange">{t('Otvorené')}</Tag>,
    },
    { title: t('Dôvod'), dataIndex: 'reason', key: 'reason' },
    {
      title: t('Rozhodnutie'),
      dataIndex: 'decision_reference',
      key: 'decision_reference',
    },
    {
      title: t('Výnimka'),
      dataIndex: 'is_exceptional',
      key: 'is_exceptional',
      render: (value) => (value ? t('Áno') : t('Nie')),
    },
    {
      title: t('Stav verzie'),
      key: 'version_state',
      render: (_, row) =>
        row.superseded_at ? (
          <Tooltip title={formatDateTime(row.superseded_at)}>
            <Tag>{t('Nahradená')}</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">{t('Platná')}</Tag>
        ),
    },
    ...(canManageInterruptions
      ? [
          {
            title: t('Akcie'),
            key: 'actions',
            render: (_, row) =>
              row.superseded_at ? null : (
                <Space wrap>
                  {!row.date_resumed ? (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => openInterruptionModal('resume', row)}
                    >
                      {t('Obnoviť štúdium')}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    onClick={() => openInterruptionModal('correct', row)}
                  >
                    {t('Vytvoriť korekciu')}
                  </Button>
                </Space>
              ),
          },
        ]
      : []),
  ];

  const isSavingInterruption =
    isCreatingInterruption ||
    isResumingInterruption ||
    isCorrectingInterruption;
  const isResume = interruptionAction?.type === 'resume';
  const isCorrection = interruptionAction?.type === 'correct';
  const resumeAfterStartRule = {
    validator: (_, value) => {
      if (!value) {
        return Promise.resolve();
      }

      const start = isResume
        ? dayjs(interruptionAction?.record?.date_started)
        : interruptionForm.getFieldValue('date_started');

      return start && !value.isAfter(start, 'day')
        ? Promise.reject(t('Návrat do štúdia musí byť po začiatku prerušenia'))
        : Promise.resolve();
    },
  };

  return (
    <>
      <CollapseBlock collapsible title={t('Trvanie štúdia')}>
        {student.state === 'Interrupted' ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title={t('Štúdium je prerušené')}
            description={t(
              'Budúce študijné termíny sú pozastavené. Po evidovaní návratu systém vytvorí nové verzie dotknutých termínov.',
            )}
          />
        ) : null}
        {['Finished', 'Expelled'].includes(student.state) ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            title={t('Štúdium je ukončené')}
            description={t(
              'Záznamy zostávajú dostupné na kontrolu, ale nemožno ich priamo meniť. Oprava vyžaduje povolený korekčný postup administrátora doktorandského štúdia.',
            )}
          />
        ) : null}
        <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label={t('Začiatok štúdia')}>
            {formatDate(student.date_study_start)}
          </Descriptions.Item>
          <Descriptions.Item label={t('Štandardný koniec štúdia')}>
            {formatDate(student.date_standard_study_end)}
          </Descriptions.Item>
          <Descriptions.Item label={t('Očakávaný koniec po prerušeniach')}>
            {student.state === 'Interrupted' && !student.date_expected_study_end
              ? t('Pozastavené počas otvoreného prerušenia')
              : formatDate(student.date_expected_study_end)}
          </Descriptions.Item>
          <Descriptions.Item label={t('Skutočný koniec štúdia')}>
            {formatDate(student.date_actual_study_end)}
          </Descriptions.Item>
        </Descriptions>
      </CollapseBlock>

      <CollapseBlock collapsible title={t('Individuálne termíny')}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title={t(
            'Zobrazená je úplná verzovaná história. Platný termín sa nemení prepísaním pôvodného záznamu.',
          )}
          description={t(
            'Zdroj vysvetľuje, či dátum vznikol z pravidla, prerušenia, dohody, korekcie alebo pôvodnej evidencie. Upravovať možno iba platnú verziu a iba s príslušným oprávnením.',
          )}
        />
        {isLoadingDeadlines ? (
          <Spinner />
        ) : (
          <Table
            rowKey="id"
            size="small"
            columns={deadlineColumns}
            dataSource={deadlines?.history || []}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: (row) => (
                <Descriptions size="small" column={{ xs: 1, md: 2 }}>
                  <Descriptions.Item label={t('Dôvod')}>
                    {row.reason || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Rozhodnutie')}>
                    {row.decision_reference || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Schválil – ID osoby')}>
                    {row.approved_by || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Vytvorené')}>
                    {formatDateTime(row.created_at)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Nahradené')}>
                    {formatDateTime(row.superseded_at)}
                  </Descriptions.Item>
                </Descriptions>
              ),
            }}
            locale={{
              emptyText: t(
                'Termíny zatiaľ neboli vytvorené. Skontrolujte začiatok, formu a stav štúdia; potom obnovte údaje o priebehu štúdia doktoranda.',
              ),
            }}
          />
        )}
      </CollapseBlock>

      <CollapseBlock collapsible title={t('Prerušenia štúdia')}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title={t('Prerušenia sa evidujú bez prepisu histórie')}
          description={t(
            'Otvorené prerušenie pozastaví budúce termíny. Návrat štúdium obnoví; chybný interval opravte novou korekciou.',
          )}
        />
        {canManageInterruptions ? (
          <div className="text-right" style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              onClick={() => openInterruptionModal('create')}
            >
              {t('Pridať prerušenie')}
            </Button>
          </div>
        ) : null}
        {isLoadingInterruptions ? (
          <Spinner />
        ) : (
          <Table
            rowKey="id"
            size="small"
            columns={interruptionColumns}
            dataSource={interruptions || []}
            pagination={false}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: (row) => (
                <Descriptions size="small" column={{ xs: 1, md: 2 }}>
                  <Descriptions.Item label={t('Poznámka')}>
                    {row.note || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Dôvod výnimky')}>
                    {row.exception_reason || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Schválil – ID osoby')}>
                    {row.approved_by || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Vytvorené')}>
                    {formatDateTime(row.created_at)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Nahrádza verziu – ID')}>
                    {row.supersedes_id || '—'}
                  </Descriptions.Item>
                </Descriptions>
              ),
            }}
            locale={{
              emptyText: canManageInterruptions
                ? t(
                    'Štúdium nemá evidované prerušenie. Ak bolo prerušenie schválené, pridajte rozhodnutie a interval.',
                  )
                : t('Štúdium nemá evidované prerušenie.'),
            }}
          />
        )}
      </CollapseBlock>

      <Modal
        title={t('Individuálna úprava termínu')}
        open={Boolean(selectedDeadline)}
        onCancel={closeDeadlineModal}
        onOk={handleDeadlineSubmit}
        okText={t('Vytvoriť novú verziu')}
        confirmLoading={isAdjustingDeadline}
        destroyOnHidden
        forceRender
      >
        <Form form={deadlineForm} layout="vertical" preserve={false}>
          <Form.Item label={t('Termín')}>
            <Input
              disabled
              value={t(
                DEADLINE_LABELS[selectedDeadline?.deadline_key] ||
                  selectedDeadline?.deadline_key ||
                  '',
              )}
            />
          </Form.Item>
          <Form.Item
            name="effective_date"
            label={t('Nový platný dátum')}
            rules={[required(t)]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="source" label={t('Typ zmeny')} rules={[required(t)]}>
            <Select
              options={[
                { value: 'agreement', label: t('Individuálna dohoda') },
                { value: 'correction', label: t('Korekcia') },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="decision_reference"
            label={t('Rozhodnutie')}
            rules={[required(t)]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="reason" label={t('Dôvod')} rules={[required(t)]}>
            <Input.TextArea rows={4} maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          isResume
            ? t('Obnovenie štúdia')
            : isCorrection
              ? t('Korekcia prerušenia')
              : t('Nové prerušenie štúdia')
        }
        open={Boolean(interruptionAction)}
        onCancel={closeInterruptionModal}
        onOk={handleInterruptionSubmit}
        okText={isCorrection ? t('Vytvoriť korekciu') : t('Uložiť')}
        confirmLoading={isSavingInterruption}
        destroyOnHidden
        forceRender
      >
        <Form form={interruptionForm} layout="vertical" preserve={false}>
          {!isResume ? (
            <>
              <Form.Item
                name="date_started"
                label={t('Začiatok prerušenia')}
                rules={[required(t)]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD.MM.YYYY"
                  minDate={
                    student.date_study_start
                      ? dayjs(student.date_study_start)
                      : undefined
                  }
                  maxDate={dayjs()}
                />
              </Form.Item>
              <Form.Item
                name="date_resumed"
                label={t('Návrat do štúdia')}
                rules={[resumeAfterStartRule]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD.MM.YYYY"
                  maxDate={dayjs()}
                />
              </Form.Item>
              <Form.Item
                name="reason"
                label={t('Dôvod prerušenia')}
                rules={[required(t)]}
              >
                <Input maxLength={64} />
              </Form.Item>
              <Form.Item
                name="is_exceptional"
                label={t('Výnimočné rozhodnutie nad 18 mesiacov')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item noStyle dependencies={['is_exceptional']}>
                {({ getFieldValue }) =>
                  getFieldValue('is_exceptional') ? (
                    <Form.Item
                      name="exception_reason"
                      label={t('Dôvod výnimky')}
                      rules={[required(t)]}
                    >
                      <Input.TextArea rows={3} maxLength={4000} showCount />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="date_resumed"
              label={t('Dátum návratu do štúdia')}
              rules={[required(t), resumeAfterStartRule]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
                maxDate={dayjs()}
              />
            </Form.Item>
          )}
          <Form.Item
            name="decision_reference"
            label={t('Rozhodnutie')}
            rules={[required(t)]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="note" label={t('Poznámka')}>
            <Input.TextArea rows={3} maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PhdStudentLifecycleSection;
